import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import AppShell from "@/components/layout/AppShell";
import { WatchlistProvider } from "@/lib/watchlist";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import DisclaimerGate from "@/components/legal/DisclaimerGate";
import ProfilePage from "@/pages/ProfilePage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import EmailLogsPage from "@/pages/EmailLogsPage";
import DashboardPage from "@/pages/DashboardPage";
import StocksPage from "@/pages/StocksPage";
import SectorsPage from "@/pages/SectorsPage";
import StockDetailPage from "@/pages/StockDetailPage";
import BestValuePage from "@/pages/BestValuePage";
import DividendsPage from "@/pages/DividendsPage";
import AiInsightsPage from "@/pages/AiInsightsPage";
import ReportsPage from "@/pages/ReportsPage";
import SystemStatusPage from "@/pages/SystemStatusPage";
import SettingsPage from "@/pages/SettingsPage";
import ReportMonitorPage from "@/pages/ReportMonitorPage";
import AdminReportPipelinePage from "@/pages/AdminReportPipelinePage";
import AdminOrbPage from "@/pages/AdminOrbPage";
import OracleTradesPage from "@/pages/OracleTradesPage";
import OracleTradesTV from "@/pages/OracleTradesTV";
import RiskLabPage from "@/pages/RiskLabPage";
import PortfolioPage from "@/pages/PortfolioPage";
import AlertsPage from "@/pages/AlertsPage";
import WatchlistPage from "@/pages/WatchlistPage";
import ComparePage from "@/pages/ComparePage";
import GlossaryPage from "@/pages/GlossaryPage";
import MarketIntelligencePage from "@/pages/MarketIntelligencePage";
import MarketIntelligenceUploadPage from "@/pages/MarketIntelligenceUploadPage";
import BacktestPage from "@/pages/BacktestPage";
import PricingPage from "@/pages/PricingPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BillingCancelPage from "@/pages/BillingCancelPage";
import AdminFeedbackPage from "@/pages/AdminFeedbackPage";
import AdminApiCostsPage from "@/pages/AdminApiCostsPage";
import AdminDeepAnalysisPage from "@/pages/AdminDeepAnalysisPage";
import AdminUsUniversePage from "@/pages/AdminUsUniversePage";
import AdminDataQualityPage from "@/pages/AdminDataQualityPage";
import UsScreenerPage from "@/pages/UsScreenerPage";
import UsStockDetailPage from "@/pages/UsStockDetailPage";
import IpoPage from "@/pages/IpoPage";
import IpoDetailPage from "@/pages/IpoDetailPage";
import TrackRecordPage from "@/pages/TrackRecordPage";
// Test 9 Fix 6 — OnboardingTour removed in favour of a simple
// dismissible welcome banner shown on the Dashboard. The 4-step modal
// tour was too intrusive for new users.
import FeedbackButton from "@/components/common/FeedbackButton";
import PWAInstallPrompt from "@/components/common/PWAInstallPrompt";
import { useAuth } from "@/lib/auth";

function AdminRedirect() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  return <Navigate to={isAdmin ? "/oracle-trades" : "/dashboard"} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* TV / Wall mode — admin-only, no sidebar, no topbar.
                Separate from the main AppShell so it fills the whole screen
                for the 4K TV-half layout. */}
            <Route path="/oracle-trades/tv" element={
              <ProtectedRoute requireAdmin>
                <OracleTradesTV />
              </ProtectedRoute>
            } />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <WatchlistProvider>
                    <TooltipProvider delayDuration={150}>
                      <AppShell>
                      <Routes>
                        <Route path="/" element={<AdminRedirect />} />
                        <Route path="/oracle-trades" element={
                          <ProtectedRoute requireAdmin>
                            <OracleTradesPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/risk-lab" element={
                          <ProtectedRoute requireAdmin>
                            <RiskLabPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/market-intelligence" element={<MarketIntelligencePage />} />
                        <Route path="/backtest" element={<BacktestPage />} />
                        <Route path="/stocks" element={<StocksPage />} />
                        <Route path="/sectors" element={<SectorsPage />} />
                        <Route path="/stocks/:companyCode" element={<StockDetailPage />} />
                        <Route path="/us-stocks" element={<UsScreenerPage />} />
                        <Route path="/us-stocks/:symbol" element={<UsStockDetailPage />} />
                        <Route path="/watchlist" element={<WatchlistPage />} />
                        <Route path="/best-value" element={<BestValuePage />} />
                        <Route path="/compare" element={<ComparePage />} />
                        <Route path="/dividends" element={<DividendsPage />} />
                        <Route path="/ai-insights" element={<AiInsightsPage />} />
                        <Route path="/ipo" element={<IpoPage />} />
                        <Route path="/ipo/:symbol" element={<IpoDetailPage />} />
                        <Route path="/track-record" element={<TrackRecordPage />} />
                        <Route path="/portfolio" element={<PortfolioPage />} />
                        <Route path="/reports" element={<ReportsPage />} />
                        <Route path="/alerts" element={<AlertsPage />} />
                        <Route path="/glossary" element={<GlossaryPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/billing/success" element={<BillingSuccessPage />} />
                        <Route path="/billing/cancel" element={<BillingCancelPage />} />
                        <Route
                          path="/system-status"
                          element={
                            <ProtectedRoute requireAdmin>
                              <SystemStatusPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute requireAdmin>
                              <SettingsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/reports"
                          element={
                            <ProtectedRoute requireAdmin>
                              <ReportMonitorPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/report-pipeline"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminReportPipelinePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/orb"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminOrbPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route
                          path="/admin/users"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminUsersPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/market-intelligence"
                          element={
                            <ProtectedRoute requireAdmin>
                              <MarketIntelligenceUploadPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/email-logs"
                          element={
                            <ProtectedRoute requireAdmin>
                              <EmailLogsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/feedback"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminFeedbackPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/api-costs"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminApiCostsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/deep-analysis"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminDeepAnalysisPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/us-universe"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminUsUniversePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/data-quality"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminDataQualityPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                      </Routes>
                      <FeedbackButton />
                      <DisclaimerGate />
                    </AppShell>
                    </TooltipProvider>
                  </WatchlistProvider>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors closeButton position="top-right" />
      <PWAInstallPrompt />
    </div>
  );
}

export default App;
